<template>
  <div class="carousel-container">
    <button
      class="carousel-arrow carousel-arrow-left"
      aria-label="Previous"
      data-testid="carousel-prev-button"
      :disabled="isAtStart"
      v-show="!isAtStart"
      @click="prev"
    >
      <zm-icon size="20" should-r-t-l>
        <ManualChevronLeftSmall />
      </zm-icon>
    </button>

    <div class="carousel-viewport" ref="viewportRef">
      <div
        class="carousel-track"
        ref="trackRef"
        :style="trackStyle"
        @mousedown="handleDragStart"
        @touchstart="handleDragStart"
      >
        <div
          v-for="(item, index) in items"
          :key="index"
          class="carousel-slide"
          :style="{ width: `${slideWidth}px` }"
        >
          <slot :item="item" :index="index">
            {{ item }}
          </slot>
        </div>
      </div>
    </div>

    <button
      class="carousel-arrow carousel-arrow-right"
      aria-label="Next"
      data-testid="carousel-next-button"  
      :disabled="isAtEnd"
      v-show="!isAtEnd"
      @click="next"
    >
      <zm-icon size="20" should-r-t-l>
        <ManualChevronRightSmall />
      </zm-icon>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { ManualChevronLeftSmall, ManualChevronRightSmall } from '@zoom/icons-vue'

const props = defineProps({
  items: {
    type: Array,
    required: true
  },
  slideWidth: {
    type: Number,
    default: 240
  },
  slideGap: {
    type: Number,
    default: 16
  },
  switchCount: {
    type: Number,
    default: 1
  }
})

const emit = defineEmits(['prev', 'next'])

const viewportRef = ref(null)
const trackRef = ref(null)
const currentIndex = ref(0)
const isDragging = ref(false)
const startX = ref(0)
const currentTranslate = ref(0)
const dragOffset = ref(0)
const isTransitioning = ref(false)
const viewportWidth = ref(0)
const isClickFocus = ref(false)

const visibleCount = computed(() => {
  if (!viewportWidth.value) return 1
  const slideSize = props.slideWidth + props.slideGap
  return Math.max(1, Math.floor((viewportWidth.value + props.slideGap) / slideSize))
})

const advanceCount = computed(() => {
  return props.switchCount > 1 ? props.switchCount : visibleCount.value
})

const maxIndex = computed(() => {
  if (!viewportWidth.value) return 0

  const totalSlidesWidth =
    props.items.length * props.slideWidth + (props.items.length - 1) * props.slideGap

  if (totalSlidesWidth <= viewportWidth.value) {
    return 0
  }

  const maxTranslate = totalSlidesWidth - viewportWidth.value
  const slideSize = props.slideWidth + props.slideGap
  return Math.ceil(maxTranslate / slideSize)
})

const isAtStart = computed(() => currentIndex.value === 0)

const isAtEnd = computed(() => {
  if (!viewportWidth.value) return false

  const currentTranslate = -(currentIndex.value * (props.slideWidth + props.slideGap))
  const totalSlidesWidth =
    props.items.length * props.slideWidth + (props.items.length - 1) * props.slideGap
  const maxTranslate = -(totalSlidesWidth - viewportWidth.value)

  return currentTranslate <= maxTranslate
})

const trackStyle = computed(() => {
  if (!viewportWidth.value) {
    return {
      display: 'flex',
      gap: `${props.slideGap}px`,
      transform: 'translateX(0px)',
      transition: 'none',
      cursor: 'grab',
      userSelect: 'none'
    }
  }

  const totalSlidesWidth =
    props.items.length * props.slideWidth + (props.items.length - 1) * props.slideGap

  if (totalSlidesWidth <= viewportWidth.value) {
    return {
      display: 'flex',
      gap: `${props.slideGap}px`,
      transform: 'translateX(0px)',
      transition: isDragging.value ? 'none' : 'transform 0.3s ease-out',
      cursor: isDragging.value ? 'grabbing' : 'grab',
      userSelect: 'none'
    }
  }

  let baseTranslate = -(currentIndex.value * (props.slideWidth + props.slideGap))
  const maxTranslate = -(totalSlidesWidth - viewportWidth.value)
  let totalTranslate = baseTranslate + dragOffset.value

  totalTranslate = Math.min(0, totalTranslate)
  totalTranslate = Math.max(maxTranslate, totalTranslate)

  return {
    display: 'flex',
    gap: `${props.slideGap}px`,
    transform: `translateX(${totalTranslate}px)`,
    transition: isDragging.value ? 'none' : 'transform 0.3s ease-out',
    cursor: isDragging.value ? 'grabbing' : 'grab',
    userSelect: 'none'
  }
})

const prev = () => {
  if (isAtStart.value || isTransitioning.value) return
  currentIndex.value = Math.max(0, currentIndex.value - advanceCount.value)
  emit('prev', currentIndex.value)
}

const next = () => {
  if (isTransitioning.value || isAtEnd.value) return
  currentIndex.value = Math.min(maxIndex.value, currentIndex.value + advanceCount.value)
  emit('next', currentIndex.value)
}

const handleDragStart = (e) => {
  if (isTransitioning.value) return

  if (e.type.includes('mouse')) {
    isClickFocus.value = true
  }

  isDragging.value = true
  startX.value = e.type.includes('mouse')
    ? e.clientX
    : e.touches[0].clientX
  currentTranslate.value = -(currentIndex.value * (props.slideWidth + props.slideGap))
  dragOffset.value = 0

  if (e.type.includes('mouse')) {
    document.addEventListener('mousemove', handleDragMove)
    document.addEventListener('mouseup', handleDragEnd)
  } else {
    document.addEventListener('touchmove', handleDragMove, { passive: false })
    document.addEventListener('touchend', handleDragEnd)
  }
}

const handleDragMove = (e) => {
  if (!isDragging.value) return

  isClickFocus.value = false

  e.preventDefault()
  const currentX = e.type.includes('mouse')
    ? e.clientX
    : e.touches[0].clientX
  dragOffset.value = currentX - startX.value
}

const handleDragEnd = () => {
  if (!isDragging.value) return

  isDragging.value = false
  isTransitioning.value = true

  document.removeEventListener('mousemove', handleDragMove)
  document.removeEventListener('mouseup', handleDragEnd)
  document.removeEventListener('touchmove', handleDragMove)
  document.removeEventListener('touchend', handleDragEnd)

  const slideSize = props.slideWidth + props.slideGap
  const dragDistance = Math.abs(dragOffset.value)
  const draggedSlides = Math.round(dragDistance / slideSize)

  if (draggedSlides > 0) {
    if (dragOffset.value > 0) {
      currentIndex.value = Math.max(0, currentIndex.value - draggedSlides)
    } else {
      currentIndex.value = Math.min(maxIndex.value, currentIndex.value + draggedSlides)
    }
    isClickFocus.value = false
  }

  dragOffset.value = 0

  setTimeout(() => {
    isTransitioning.value = false
    isClickFocus.value = false
  }, 200)
}

const updateViewportWidth = () => {
  if (viewportRef.value) {
    viewportWidth.value = viewportRef.value.offsetWidth
  }
}

const handleResize = () => {
  updateViewportWidth()
  if (currentIndex.value > maxIndex.value) {
    currentIndex.value = maxIndex.value
  }
}

const handleViewportScroll = () => {
  if (viewportRef.value) {
    viewportRef.value.scrollLeft = 0
  }
}

const handleFocusIn = (e) => {
  if (isClickFocus.value) return

  if (!trackRef.value || !viewportWidth.value) return

  const slide = e.target.closest('.carousel-slide')
  if (!slide) return

  const slides = Array.from(trackRef.value.querySelectorAll('.carousel-slide'))
  const slideIndex = slides.indexOf(slide)
  if (slideIndex === -1) return

  const slidePosition = slideIndex * (props.slideWidth + props.slideGap)
  const currentPosition = -(currentIndex.value * (props.slideWidth + props.slideGap))
  const visibleStart = -currentPosition
  const visibleEnd = visibleStart + viewportWidth.value

  if (slidePosition < visibleStart) {
    currentIndex.value = slideIndex
  } else if (slidePosition + props.slideWidth > visibleEnd) {
    const slideSize = props.slideWidth + props.slideGap
    const targetIndex = Math.ceil(
      (slidePosition + props.slideWidth - viewportWidth.value) / slideSize
    )
    currentIndex.value = Math.min(maxIndex.value, targetIndex)
  }
}

watch(viewportRef, (viewport) => {
  if (viewport) {
    updateViewportWidth()
    viewport.addEventListener('scroll', handleViewportScroll, { passive: true })
    viewport.addEventListener('focusin', handleFocusIn)
  }
})

onMounted(() => {
  updateViewportWidth()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)

  if (viewportRef.value) {
    viewportRef.value.removeEventListener('scroll', handleViewportScroll)
    viewportRef.value.removeEventListener('focusin', handleFocusIn)
  }

  document.removeEventListener('mousemove', handleDragMove)
  document.removeEventListener('mouseup', handleDragEnd)
  document.removeEventListener('touchmove', handleDragMove)
  document.removeEventListener('touchend', handleDragEnd)
})
</script>

<style scoped lang="scss">
.carousel-container {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
}

.carousel-viewport {
  flex: 1;
  overflow: hidden;
  position: relative;
  padding-bottom: 5px;
}

.carousel-track {
  will-change: transform;
  justify-content: flex-start;
}

.carousel-slide {
  flex-shrink: 0;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  z-index: 10;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background-color: rgb(0 0 0 / 0.42);
  color: white;
  cursor: pointer;
  transform: translateY(-50%);

  &:hover {
    background-color: rgb(0 0 0 / 0.56);
    color: white;
  }

  &:active {
    background-color: rgb(0 0 0 / 0.56);
    color: white;
  }

  &:focus-visible {
    box-shadow: none !important;
    outline: 2px solid #0e72ed;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &-left {
    left: 8px;
  }

  &-right {
    right: 8px;
  }
}
</style>
